// CLI v0.8 — `cloudgrid secrets get <key> [<name>]`. Spec v17 §13.9.
//
// Hard rule from kickoff: secrets `get` returns metadata only — never the
// value. We confirm presence by listing keys (server-side endpoint already
// strips values) and report "set" or "not set".

import { Command } from 'commander';
import { Command as CommanderCommand } from 'commander';
import { getV2Client, type IV2Client } from '../../api/v2-client.js';
import { handleV2Error } from '../../handle-error.js';
import { V2ApiError } from '../../api/v2-errors.js';
import { resolveEntityTarget } from '../../services/resolve-entity-target.js';

interface SecretsListResponse { secrets: Array<{ key: string }> }

export interface RunSecretsGetOpts {
  client: IV2Client;
  name: string | undefined;
  key: string;
  cwd?: string;
}

export async function runSecretsGet(opts: RunSecretsGetOpts): Promise<void> {
  const target = await resolveEntityTarget(opts.client, opts.name, opts.cwd);
  const resp = await opts.client.get<SecretsListResponse>(
    `/api/v2/runtimes/${encodeURIComponent(target.entity_id)}/secrets`,
  );
  const exists = resp.secrets.some((s) => s.key === opts.key);
  if (!exists) {
    throw new V2ApiError(
      404, 'KEY_NOT_FOUND',
      `Secret '${opts.key}' is not set on ${target.name}.`,
    );
  }
  process.stdout.write(
    `${opts.key} is set on ${target.name}. Values are not viewable from the CLI.\n`,
  );
}

export const secretsGetCommand: CommanderCommand = new Command('get')
  .description('Confirm a secret key exists (the value is never returned)')
  .argument('<key>', 'Secret key')
  .argument('[name]', 'Entity slug or id (defaults to the entity linked in this directory)')
  .action(async (key: string, name: string | undefined) => {
    try {
      const client = await getV2Client();
      await runSecretsGet({ client, name, key });
    } catch (err) {
      await handleV2Error(err);
    }
  });
